import {
  PaidCategoryPageQuery,
  PaidCategoryPageQueryVariables,
  PaidContentfulCategoryPageQuery,
  PaidContentfulCategoryPageQueryVariables,
  SearchFilters,
} from "@outschool/gql-frontend-generated";
import { useLocale } from "@outschool/localization";
import { gql, useQuery } from "@outschool/ui-apollo";
import _ from "lodash";

const contentfulCategoryPageQuery = gql`
  query PaidContentfulCategoryPage($categorySlug: String!, $locale: String) {
    contentfulCategoryPageCollection(
      limit: 1
      skip: 0
      preview: false
      locale: $locale
      where: { categorySlug: $categorySlug }
    ) {
      items {
        featuredClassesOverrideSlug
        heroTitle
        heroImage {
          url(transform: { quality: 85, width: 503, height: 335, format: WEBP })
        }
      }
    }
  }
`;

const categoryPageQuery = gql`
  query PaidCategoryPage($slug: String!) {
    categoryPage(slug: $slug) {
      title
      searchFilters {
        adminTags
        age
        capacityMax
        capacityMin
        delivery
        dow
        enabledBooleanFilters
        endBy
        endByTime
        format
        fundingPrograms
        hasFilledOutUniqueLearningNeeds
        languageOfInstruction
        multiTermQuery
        order
        pricePerMeetingMax
        pricePerMeetingMin
        q
        startAfter
        startAfterTime
        startBefore
        theme
        time
        timeOfDay
        timeZone
        uniqueLearningNeeds
      }
    }
  }
`;

export type PaidClp = {
  title: string;
  searchFilters: SearchFilters;
  contentfulOverrides?: {
    heroImageUrl?: string | null;
    heroTitle?: string | null;
    featuredClassesOverrideSlug?: string | null;
  };
};
type GetPaidClpQueryResponse = {
  loading: boolean;
  categoryPage?: {
    title: string;
    searchFilters: SearchFilters;
    contentfulOverrides?: {
      heroImageUrl?: string | null;
      heroTitle?: string | null;
      featuredClassesOverrideSlug?: string | null;
    };
  };
};

export const useGetPaidClpQuery = (
  categorySlug: string,
): GetPaidClpQueryResponse => {
  const locale = useLocale();

  const {
    loading: contentfulDataLoading,
    data: contentfulData,
    error: contentfulError,
  } = useQuery<
    PaidContentfulCategoryPageQuery,
    PaidContentfulCategoryPageQueryVariables
  >(contentfulCategoryPageQuery, { variables: { categorySlug, locale } });
  const [contentfulOverrides] =
    contentfulData?.contentfulCategoryPageCollection?.items || [];
  const { featuredClassesOverrideSlug } = contentfulOverrides || {};
  const paidClpQuerySlug = featuredClassesOverrideSlug || categorySlug;
  if (contentfulError) {
    OsPlatform.captureError(contentfulError);
  }

  const {
    loading: clpLoading,
    data: clpData,
    error: clpQueryError,
  } = useQuery<PaidCategoryPageQuery, PaidCategoryPageQueryVariables>(
    categoryPageQuery,
    {
      variables: { slug: paidClpQuerySlug },
    },
  );
  if (clpQueryError) {
    OsPlatform.captureError(clpQueryError);
    return { loading: false };
  }

  if (contentfulDataLoading || clpLoading) {
    return { loading: true };
  }
  const { title, searchFilters } = clpData?.categoryPage || {};

  if (contentfulOverrides) {
    return {
      loading: false,
      categoryPage: {
        title: title!,
        searchFilters: {
          ..._.omit(searchFilters || {}, ["__typename"]),
        },
        contentfulOverrides: {
          heroImageUrl: contentfulOverrides.heroImage?.url,
          heroTitle: contentfulOverrides.heroTitle,
          featuredClassesOverrideSlug:
            contentfulOverrides.featuredClassesOverrideSlug,
        },
      },
    };
  }

  return {
    loading: false,
    categoryPage: {
      title: title!,
      searchFilters: searchFilters || {},
    },
  };
};
